package com.sddamico.draftmachine.ds.core

import androidx.compose.runtime.Composable
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.backgroundImageRaw
import com.sddamico.draftmachine.ds.shadowPlate
import com.sddamico.draftmachine.ds.shadowRaised
import com.sddamico.draftmachine.ds.textureCta
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span

/** Brushed-steel panel, deeper raised panel, or polished steel plate (dark text). */
enum class CardVariant { Panel, Raised, Plate }

/**
 * A riveted metal panel — the primary content surface. Ports `components/core/Card.jsx`.
 *
 * @param riveted stamps four corner bolts.
 * @param heat overlays the "on the clock" energized-glass wash.
 * @param padding inner padding (defaults to `--space-6`).
 * @param content the card body (rendered in a relatively-positioned layer above the overlays).
 */
@Composable
fun Card(
  modifier: Modifier = Modifier,
  variant: CardVariant = CardVariant.Panel,
  riveted: Boolean = false,
  heat: Boolean = false,
  padding: CSSLengthOrPercentageNumericValue = DmSpacing.space6,
  content: @Composable () -> Unit,
) {
  val mod = Modifier
    .styleModifier {
      property("position", "relative")
      property("overflow", "hidden")
    }
    .borderRadius(DmEffects.radiusMd)
    .padding(padding)
    .cardSurface(variant)
    .then(modifier)

  Div(attrs = mod.toAttrs()) {
    if (heat) {
      Div(
        attrs = Modifier
          .styleModifier {
            property("position", "absolute")
            property("inset", "0")
            property("pointer-events", "none")
          }
          .backgroundImageRaw(DmEffects.gradientHeat)
          .toAttrs { attr("aria-hidden", "true") }
      ) {}
    }
    if (riveted) {
      CardRivets.forEach { RivetDot(it) }
    }
    Div(attrs = Modifier.styleModifier { property("position", "relative") }.toAttrs()) {
      content()
    }
  }
}

/** The stamped surface for each card variant (fill, cast-metal texture, text, border, shadow). */
internal fun Modifier.cardSurface(variant: CardVariant): Modifier = when (variant) {
  CardVariant.Panel -> this
    .backgroundColor(DmColor.surfacePanel)
    .textureCta()
    .color(DmColor.text)
    .border(1.px, LineStyle.Solid, DmColor.border)
    .shadowPlate()

  CardVariant.Raised -> this
    .backgroundColor(DmColor.surfaceRaised)
    .textureCta()
    .color(DmColor.text)
    .border(1.px, LineStyle.Solid, DmColor.borderStrong)
    .shadowRaised()

  CardVariant.Plate -> this
    .backgroundColor(DmColor.surfacePlate)
    .textureCta()
    .color(DmColor.textOnPlate)
    .border(1.px, LineStyle.Solid, DmColor.steel300)
    .shadowPlate()
}

/** One corner bolt — an absolutely-positioned 6px round stud (steel-300 → steel-600 radial). */
private class RivetInset(val top: String, val right: String, val bottom: String, val left: String)

private val CardRivets = listOf(
  RivetInset(top = "8px", right = "auto", bottom = "auto", left = "8px"), // top-left
  RivetInset(top = "8px", right = "8px", bottom = "auto", left = "auto"), // top-right
  RivetInset(top = "auto", right = "auto", bottom = "8px", left = "8px"), // bottom-left
  RivetInset(top = "auto", right = "8px", bottom = "8px", left = "auto"), // bottom-right
)

@Composable
private fun RivetDot(pos: RivetInset) {
  Span(
    attrs = Modifier
      .styleModifier {
        property("position", "absolute")
        property("width", "6px")
        property("height", "6px")
        property("border-radius", "50%")
        property("top", pos.top)
        property("right", pos.right)
        property("bottom", pos.bottom)
        property("left", pos.left)
        property("background", "radial-gradient(circle at 35% 30%, #B0BFCC, #4B5A68)")
        property("box-shadow", "inset 0 -1px 1px rgba(0,0,0,.5)")
      }
      .toAttrs { attr("aria-hidden", "true") }
  ) {}
}
